/**
 * Computed-tags resource — typed wrappers over the LIVE `/v1/iot/computed-tags`
 * surface (the CRUD Arun's backend already ships:
 * platform-core/internal/iot/computedtags/handler.go).
 *
 * A computed tag is a tag derived from one or more input tags via a math.js
 * expression, evaluated by the computed-tag worker whenever an input publishes
 * (trigger `on_input_change`) or on a schedule. Its output lands in the readings
 * hypertable under the def name on the per-org `_computed` device, so it is read
 * back through the ordinary `/v1/iot/tags/{id}/latest|values` path like any tag.
 *
 * Note the wire response keys `tag_id` (NOT `id`) — the server's def shape in
 * computedtags/types.go — which differs from the Phase-0 in-memory
 * `lib/compute/types.ts` `ComputedTagDef` (`id`). The org is taken from the
 * X-Org-Id context on the server (Create ignores any body org), so no org_id is
 * sent here.
 */

import { api } from "@/lib/api-client";
import type { Paginated } from "./types";
import type { ComputedTagDef, ComputedTagInput, ComputedTagTrigger } from "@/lib/compute/types";
import type { DataKind } from "@foundry/foundry-plant";

/** `POST /v1/iot/computed-tags` body — the create shape (mirrors createReq). */
export interface ComputedTagCreateBody {
  name: string;
  description?: string;
  outputUnit?: string;
  outputDataKind: DataKind;
  inputs: ComputedTagInput[];
  expression: string;
  trigger: ComputedTagTrigger;
  enabled?: boolean;
  presetId?: string;
}

/** The wire shape returned by the computed-tags endpoints (mirrors the Go def). */
export interface ComputedTagWire {
  tag_id: string;
  name: string;
  description?: string;
  outputUnit?: string;
  outputDataKind: DataKind;
  inputs: ComputedTagInput[];
  expression: string;
  trigger: ComputedTagTrigger;
  enabled: boolean;
  presetId?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Wire → the UI's `ComputedTagDef` (lib/compute/types.ts), which keys on `id`.
 *
 * The mismatch is deliberate on both sides: the server's control-plane fields are
 * snake_case (`tag_id`, `org_id`) while its payload stays camelCase, and the UI
 * type predates the endpoint. Adapting at THIS boundary keeps the rename out of
 * `store.tsx`, `__fixtures.ts`, `routes/iot/computed-tags.tsx`, `EntityAliasPicker`
 * and `copilot/dispatch.ts`.
 */
export function toComputedTagDef(w: ComputedTagWire): ComputedTagDef {
  return {
    id: w.tag_id,
    name: w.name,
    description: w.description,
    outputUnit: w.outputUnit,
    outputDataKind: w.outputDataKind,
    inputs: w.inputs,
    expression: w.expression,
    trigger: w.trigger,
    enabled: w.enabled,
    presetId: w.presetId,
    createdAt: w.createdAt,
    updatedAt: w.updatedAt,
  };
}

/** `ComputedTagDef` → the create/update body (drops client- and server-only fields). */
export function toComputedTagBody(
  d: Omit<ComputedTagDef, "id" | "createdAt" | "updatedAt">,
): ComputedTagCreateBody {
  return {
    name: d.name,
    description: d.description,
    outputUnit: d.outputUnit,
    outputDataKind: d.outputDataKind,
    inputs: d.inputs,
    expression: d.expression,
    trigger: d.trigger,
    enabled: d.enabled,
    presetId: d.presetId,
  };
}

export const computedTags = {
  /** List the active org's computed tags. `GET /v1/iot/computed-tags`. */
  list: () => api.get<Paginated<ComputedTagWire>>("/v1/iot/computed-tags"),
  /** One computed tag. `GET /v1/iot/computed-tags/{tag_id}`. */
  get: (tagId: string) => api.get<ComputedTagWire>(`/v1/iot/computed-tags/${tagId}`),
  /** Create a computed tag. `POST /v1/iot/computed-tags` → 201. Org from context. */
  create: (body: ComputedTagCreateBody) =>
    api.post<ComputedTagWire>("/v1/iot/computed-tags", body),
  /** Replace a computed tag def. `PUT /v1/iot/computed-tags/{tag_id}` → 200. */
  update: (tagId: string, body: ComputedTagCreateBody) =>
    api.put<ComputedTagWire>(`/v1/iot/computed-tags/${tagId}`, body),
  /** Delete a computed tag. `DELETE /v1/iot/computed-tags/{tag_id}` → 204. */
  remove: (tagId: string) => api.delete<void>(`/v1/iot/computed-tags/${tagId}`),
};
